<script setup lang="ts">
// A modal that asks the user to type the environment's name before a destructive action (FR-30).
import { nextTick, ref, watch } from 'vue';
import { confirmationMatches } from '../actions/guards';

const props = defineProps<{ open: boolean; name: string; title: string; description: string; confirmLabel: string }>();
const emit = defineEmits<{ confirm: [typed: string]; cancel: [] }>();

const dlg = ref<HTMLDialogElement>();
const input = ref<HTMLInputElement>();
const typed = ref('');

watch(
  () => props.open,
  async (open) => {
    typed.value = '';
    await nextTick();
    if (!dlg.value) return;
    if (open && !dlg.value.open) {
      dlg.value.showModal?.() ?? dlg.value.setAttribute('open', '');
      input.value?.focus();
    } else if (!open && dlg.value.open) {
      dlg.value.close?.() ?? dlg.value.removeAttribute('open');
    }
  },
  { immediate: true },
);

function submit() {
  if (confirmationMatches(typed.value, props.name)) emit('confirm', typed.value);
}
</script>

<template>
  <dialog ref="dlg" aria-labelledby="confirm-title" @cancel.prevent="emit('cancel')">
    <form method="dialog" @submit.prevent="submit">
      <h2 id="confirm-title">{{ title }}</h2>
      <p>{{ description }}</p>
      <slot />
      <div class="field">
        <label for="confirm-name">Type <code>{{ name }}</code> to confirm</label>
        <input id="confirm-name" ref="input" v-model="typed" autocomplete="off" spellcheck="false" data-testid="confirm-input" />
      </div>
      <div class="actions-row">
        <button type="submit" class="danger" :disabled="!confirmationMatches(typed, name)" data-testid="confirm-submit">{{ confirmLabel }}</button>
        <button type="button" @click="emit('cancel')">Cancel</button>
      </div>
    </form>
  </dialog>
</template>
